import styled from 'styled-components';

export const EditorContainer = styled.div`
  height: 100%;
  background: linear-gradient(180deg, #0f1419 0%, #1a1f2e 100%);
  display: flex;
  flex-direction: column;
  padding: 0;
  border-radius: 0;
  overflow: hidden;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

export const Toolbar = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(30, 35, 50, 0.95);
  position: sticky;
  top: 0;
  flex-wrap: wrap;
  align-items: center;
  z-index: 1;
  border-radius: 0;
  border-bottom: 1px solid rgba(100, 150, 200, 0.15);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
`;

export const ToolGroup = styled.div`
  display: flex;
  gap: 6px;
  padding-right: 16px;
  border-right: 1px solid rgba(100, 150, 200, 0.15);
  &:last-child {
    border-right: none;
    padding-right: 0;
  }
`;

export const Button = styled.button`
  padding: 8px 14px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  cursor: pointer;
  color: #a5b4fc;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(99, 102, 241, 0.25) 100%);
    border-color: rgba(99, 102, 241, 0.5);
    transform: translateY(-1px);
  }

  &:active {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(99, 102, 241, 0.3) 100%);
    transform: translateY(0);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const Select = styled.div`
  position: relative;
  display: inline-block;
`;

export const SelectButton = styled(Button)`
  min-width: 90px;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:after {
    content: '';
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 4px solid rgba(255, 255, 255, 0.8);
    margin-left: 4px;
  }
`;

export const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: linear-gradient(180deg, rgba(30, 40, 60, 0.98) 0%, rgba(25, 35, 55, 0.99) 100%);
  border: 1px solid rgba(100, 150, 200, 0.2);
  border-radius: 6px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  z-index: 1000;
  min-width: 180px;
  display: ${({ $isOpen }) => $isOpen ? 'block' : 'none'};
  max-height: 300px;
  overflow-y: auto;
  margin-top: 4px;
`;

export const MenuItem = styled.button`
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: rgba(180, 200, 230, 0.9);
  transition: background 0.2s ease;

  &:hover {
    background: linear-gradient(90deg, rgba(60, 100, 160, 0.2) 0%, rgba(40, 55, 85, 0.15) 100%);
  }

  span.shortcut {
    color: rgba(140, 170, 210, 0.7);
    margin-left: 32px;
    font-size: 11px;
  }

  ${props => props.fontFamily && `
    font-family: ${props.fontFamily};
  `}
`;

export const ColorButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px 8px;
  height: 28px;
  position: relative;
  
  span {
    line-height: 1;
    display: flex;
    align-items: center;
  }
`;

export const Editor = styled.div`
  flex: 1;
  padding: 24px;
  margin: 16px;
  border: 1px solid rgba(100, 150, 200, 0.15);
  border-radius: 12px;
  overflow-y: auto;
  text-align: left;
  cursor: text;
  background: linear-gradient(180deg, rgba(30, 35, 50, 0.6) 0%, rgba(25, 30, 45, 0.8) 100%);
  color: #e8eaed;
  word-wrap: break-word;
  font-size: 15px;
  line-height: 1.7;
  box-shadow: inset 0 2px 10px rgba(0, 0, 0, 0.15);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  /* Style for the contentEditable div */
  & > div[contenteditable="true"] {
    height: 100%;
    max-height: 100%;
    overflow-y: auto;
    position: static !important;
    padding: 0;
    margin: 0;
    pointer-events: auto;
  }
  
  /* Force stable positioning */
  * {
    position: static !important;
    transform: none !important;
    transition: none !important;
    line-height: 1.7 !important;
    font-size: inherit;
  }
  
  /* Disable hover effects that might cause repositioning */
  &:hover * {
    transition: none !important;
    transform: none !important;
  }
  
  /* Stabilize highlighted text */
  [style*="background-color"] {
    display: inline !important;
    position: static !important;
    margin: 0 !important;
    padding: 0 !important;
    border: none !important;
    transform: none !important;
    vertical-align: baseline !important;
    line-height: inherit !important;
  }
  
  /* Ensure content doesn't shift */
  p, div, span {
    margin: 0 !important;
    padding: 0 !important;
    line-height: inherit !important;
  }

  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(99, 102, 241, 0.5);
  }

  &:focus {
    outline: none;
    border-color: #4a90e2;
  }
  
  img {
    cursor: pointer;
    max-width: 100%;
    
    &.selected {
      outline: 2px solid #4a90e2;
      outline-offset: 2px;
    }
  }
  
  /* Add style for selected text */
  ::selection {
    background-color: #4a90e2;
    color: white;
  }
`;

export const ColorPalette = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: linear-gradient(180deg, rgba(30, 40, 60, 0.98) 0%, rgba(25, 35, 55, 0.99) 100%);
  border: 1px solid rgba(100, 150, 200, 0.2);
  border-radius: 8px;
  padding: 12px;
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  z-index: 1000;
  width: 280px;
  display: none;
`;

export const ColorPickerContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 28px;
  &:hover ${ColorPalette} {
    display: grid;
  }
`;

export const ColorSwatch = styled.button`
  width: 22px;
  height: 22px;
  background-color: ${props => props.color};
  border: ${props => props.$isSelected ? '2px solid white' : '1px solid rgba(255, 255, 255, 0.2)'};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.1s ease;
  padding: 0;
  margin: 0;
  outline: none;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 0 5px rgba(255, 255, 255, 0.3);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

export const FileMenuContainer = styled.div`
  position: relative;
  margin-right: 16px;
`;

export const FileMenuButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 90px;
  justify-content: space-between;
  
  &:after {
    content: '';
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid rgba(165, 180, 252, 0.8);
    margin-left: 6px;
  }
`;

export const FileMenuDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: linear-gradient(180deg, rgba(30, 40, 60, 0.98) 0%, rgba(25, 35, 55, 0.99) 100%);
  border: 1px solid rgba(100, 150, 200, 0.2);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  z-index: 1000;
  min-width: 200px;
  display: ${({ $isOpen }) => $isOpen ? 'block' : 'none'};
  margin-top: 6px;
  padding: 6px 0;
  overflow: hidden;
`;

export const MenuDivider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(100, 150, 200, 0.2), transparent);
  margin: 6px 12px;
`;

export const ImageMenuContainer = styled.div`
  position: relative;
`;

export const ImageButton = styled(Button)`
  svg {
    width: 16px;
    height: 16px;
  }
`;

export const ImageDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: linear-gradient(180deg, rgba(30, 40, 60, 0.98) 0%, rgba(25, 35, 55, 0.99) 100%);
  border: 1px solid rgba(100, 150, 200, 0.2);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  z-index: 1000;
  min-width: 220px;
  display: ${({ $isOpen }) => $isOpen ? 'block' : 'none'};
  margin-top: 6px;
  padding: 6px 0;
  overflow: hidden;
`;

export const SaveDialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

export const SaveDialog = styled.div`
  background: linear-gradient(180deg, rgba(30, 40, 60, 0.98) 0%, rgba(20, 28, 45, 0.99) 100%);
  color: #e8eaed;
  padding: 32px;
  border-radius: 16px;
  width: 420px;
  max-width: 90%;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(100, 150, 200, 0.15);
  
  h3 {
    margin-top: 0;
    margin-bottom: 24px;
    font-size: 20px;
    color: #ffffff;
    text-align: center;
    font-weight: 600;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  }
`;

export const SaveDialogRow = styled.div`
  margin-bottom: 20px;
  
  label {
    display: block;
    margin-bottom: 10px;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.7);
    font-weight: 500;
  }
  
  input, select {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid rgba(100, 150, 200, 0.2);
    border-radius: 8px;
    font-size: 14px;
    background: rgba(30, 40, 60, 0.6);
    color: #e8eaed;
    transition: all 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: rgba(99, 102, 241, 0.5);
      box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
    }
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }
  }
  
  select {
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23a5b4fc' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
  }
`;

export const SaveDialogButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 28px;
  gap: 12px;
`;

export const SaveDialogButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:first-child {
    background: rgba(50, 60, 80, 0.6);
    color: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(100, 150, 200, 0.2);
    
    &:hover {
      background: rgba(60, 75, 100, 0.7);
      border-color: rgba(100, 150, 200, 0.3);
    }
  }
  
  &:last-child {
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    color: white;
    border: none;
    box-shadow: 0 4px 15px rgba(99, 102, 241, 0.3);
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
    }
  }
`;

export const GeneratingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

export const GeneratingSpinner = styled.div`
  border: 4px solid rgba(99, 102, 241, 0.2);
  border-top: 4px solid #8b5cf6;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const GeneratingText = styled.div`
  margin-top: 24px;
  font-size: 16px;
  color: #e8eaed;
  background: linear-gradient(180deg, rgba(30, 40, 60, 0.9) 0%, rgba(25, 35, 55, 0.95) 100%);
  padding: 14px 28px;
  border-radius: 10px;
  border: 1px solid rgba(100, 150, 200, 0.15);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

export const OpenFileButton = styled.button`
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%);
  color: #a5b4fc;
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(99, 102, 241, 0.25) 100%);
    border-color: rgba(99, 102, 241, 0.5);
  }
`;

export const ShareOptionsContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

export const ShareOption = styled.div`
  flex: 1;
  padding: 14px;
  background: ${props => props.$isActive ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' : 'rgba(50, 60, 80, 0.5)'};
  color: #fff;
  border-radius: 10px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${props => props.$isActive ? 'transparent' : 'rgba(100, 150, 200, 0.2)'};
  
  &:hover {
    background: ${props => props.$isActive ? 'linear-gradient(135deg, #7577f2 0%, #9d6ef7 100%)' : 'rgba(60, 75, 100, 0.6)'};
    transform: translateY(-2px);
  }
  
  svg {
    display: block;
    width: 24px;
    height: 24px;
    margin: 0 auto 8px;
  }
`;

export const ShareLinkContainer = styled.div`
  display: flex;
  align-items: center;
  
  input {
    flex: 1;
    padding: 12px 16px;
    border: 1px solid rgba(100, 150, 200, 0.2);
    border-radius: 8px 0 0 8px;
    font-size: 14px;
    background: rgba(30, 40, 60, 0.6);
    color: #e8eaed;
  }
`;

export const CopyButton = styled.button`
  padding: 12px 16px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  border: none;
  border-radius: 0 8px 8px 0;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(135deg, #7577f2 0%, #9d6ef7 100%);
  }
`;

export const ShareDialog = styled.div`
  background: linear-gradient(180deg, rgba(30, 40, 60, 0.98) 0%, rgba(20, 28, 45, 0.99) 100%);
  color: #e8eaed;
  padding: 32px;
  border-radius: 16px;
  width: 420px;
  max-width: 90%;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(100, 150, 200, 0.15);
  
  h3 {
    margin-top: 0;
    margin-bottom: 24px;
    font-size: 20px;
    color: #ffffff;
    text-align: center;
    font-weight: 600;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  }
`;

export const ContextMenu = styled.div`
  position: absolute;
  background: linear-gradient(180deg, rgba(30, 40, 60, 0.98) 0%, rgba(25, 35, 55, 0.99) 100%);
  border: 1px solid rgba(100, 150, 200, 0.2);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  z-index: 1000;
  min-width: 200px;
  padding: 6px 0;
  display: ${({ $isOpen }) => $isOpen ? 'block' : 'none'};
`;

export const ContextMenuItem = styled.button`
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: rgba(180, 200, 230, 0.9);
  transition: background 0.2s ease;

  &:hover {
    background: linear-gradient(90deg, rgba(60, 100, 160, 0.2) 0%, rgba(40, 55, 85, 0.15) 100%);
  }

  span.shortcut {
    color: rgba(140, 170, 210, 0.6);
    margin-left: 32px;
    font-size: 11px;
  }
`;

export const ToolDropdown = styled.div`
  position: relative;
  display: inline-block;
`;

export const ToolDropdownButton = styled(Button)`
  min-width: 44px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: ${props => props.$isOpen ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.25) 0%, rgba(139, 92, 246, 0.25) 100%)' : 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)'};
  border: 1px solid ${props => props.$isOpen ? 'rgba(99, 102, 241, 0.5)' : 'rgba(99, 102, 241, 0.3)'};
  
  &:hover {
    background: ${props => props.$isOpen ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)' : 'linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(99, 102, 241, 0.25) 100%)'};
    border-color: ${props => props.$isOpen ? 'rgba(99, 102, 241, 0.6)' : 'rgba(99, 102, 241, 0.5)'};
  }
  
  &:after {
    content: '';
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid rgba(165, 180, 252, 0.8);
    margin-left: 8px;
  }
`;

export const ToolDropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: linear-gradient(180deg, rgba(30, 40, 60, 0.98) 0%, rgba(25, 35, 55, 0.99) 100%);
  border: 1px solid rgba(100, 150, 200, 0.2);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  z-index: 9999;
  min-width: 140px;
  display: ${({ $isOpen }) => $isOpen ? 'block' : 'none'};
  margin-top: 6px;
  padding: 8px;
`;

export const ToolItemRow = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
  &:last-child {
    margin-bottom: 0;
  }
`;

export const ToolDropdownButtonEnhanced = styled(ToolDropdownButton)`
  background: ${props => props.$isOpen ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.25) 0%, rgba(139, 92, 246, 0.25) 100%)' : 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)'};
  border: 1px solid ${props => props.$isOpen ? 'rgba(99, 102, 241, 0.5)' : 'rgba(99, 102, 241, 0.3)'};
  min-width: 54px; 
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px 0 10px;
  position: relative;
  
  &:hover {
    background: ${props => props.$isOpen ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)' : 'linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(99, 102, 241, 0.25) 100%)'};
    border-color: ${props => props.$isOpen ? 'rgba(99, 102, 241, 0.6)' : 'rgba(99, 102, 241, 0.5)'};
  }
`;

// Help popup styled components
export const HelpPopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  z-index: 10000;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const HelpPopup = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: linear-gradient(180deg, rgba(30, 40, 60, 0.98) 0%, rgba(20, 28, 45, 0.99) 100%);
  border: 1px solid rgba(100, 150, 200, 0.15);
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
  width: 850px;
  max-width: 90vw;
  max-height: 80vh;
  z-index: 10001;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const HelpPopupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(100, 150, 200, 0.15);
  background: rgba(25, 35, 55, 0.6);
`;

export const HelpPopupTitle = styled.h2`
  margin: 0;
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

export const CloseButton = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  cursor: pointer;
  color: rgba(165, 180, 252, 0.8);
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.2);
    color: #a5b4fc;
  }
`;

export const HelpPopupContent = styled.div`
  padding: 24px;
  overflow-y: auto;
  color: #e8eaed;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(99, 102, 241, 0.5);
  }
`;

export const HelpSection = styled.div`
  margin-bottom: 28px;
`;

export const HelpSectionTitle = styled.h3`
  font-size: 16px;
  margin-bottom: 12px;
  color: #a5b4fc;
  border-bottom: 1px solid rgba(100, 150, 200, 0.15);
  padding-bottom: 8px;
  font-weight: 600;
`;

export const HelpSectionContent = styled.div`
  font-size: 14px;
  line-height: 1.7;
  color: rgba(232, 234, 237, 0.85);
`;

export const HelpFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid rgba(100, 150, 200, 0.15);
  background: rgba(25, 35, 55, 0.6);
`;

export const HelpCloseButton = styled.button`
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 15px rgba(99, 102, 241, 0.3);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
  }
`;

export const ComponentImage = styled.div`
  background: rgba(30, 40, 60, 0.5);
  border: 1px solid rgba(100, 150, 200, 0.15);
  border-radius: 10px;
  padding: 16px;
  margin: 12px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const ShortcutTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 12px 0;
  
  th, td {
    border: 1px solid rgba(100, 150, 200, 0.15);
    padding: 10px 14px;
    text-align: left;
  }
  
  th {
    background: rgba(30, 40, 60, 0.5);
    color: #a5b4fc;
    font-weight: 600;
  }
  
  td {
    color: rgba(232, 234, 237, 0.85);
  }
  
  tr:hover td {
    background: rgba(40, 55, 80, 0.3);
  }
  
  tr:nth-child(even) {
    background-color: rgba(255, 255, 255, 0.03);
  }
`; 